import { useState, useSyncExternalStore } from 'react';
import { BrandLogo } from './brand.js';
import { Button } from './controls.js';
import { Console } from './Console.js';
import { t, type Key } from './i18n/index.js';
import { IconMonitor, IconMoon, IconSmartphone, IconSun } from './icons.js';
import { Centered } from './layout.js';
import { Pairing, type PairingProps } from './Pairing.js';
import type { Connection, ErrorReason, SessionPhase } from './session.js';
import type { SetupStore } from './store.js';
import { applyTheme, loadTheme, type Theme } from './theme.js';

/** The phases of the page: pairing, the console, and the ways it can stop. */
export function App({ store }: { store: SetupStore }) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const { phase } = state;
  // Losing the relay no longer stops the page: it rejoins by itself, so what was on screen
  // stays, with the connection shown where it matters.
  const consoleShown = phase.kind === 'paired' || phase.kind === 'away';

  return (
    <div className="shell">
      <header className="topbar">
        <div className={`topbar__in${consoleShown ? '' : ' topbar__in--narrow'}`}>
          <div className="brand"><BrandLogo label={t('brand.name')} /><span className="brand__sep" aria-hidden="true">/</span><span className="brand__page">{t('brand.page')}</span></div>
          {consoleShown ? <LivePill store={store} online={phase.kind === 'paired'} connection={state.connection} /> : <span />}
          <div className="topbar__end"><ThemeSwitch /></div>
        </div>
      </header>
      {consoleShown ? <Console store={store} state={state} /> : <Plain store={store} phase={phase} renewing={state.renewing} />}
    </div>
  );
}

/**
 * Which iPhone, and how it is doing. The state word sits in a slot of fixed width, so the pill
 * keeps its size whatever the phone or the relay does.
 */
function LivePill({ store, online, connection }: { store: SetupStore; online: boolean; connection: Connection }) {
  const device = store.view?.config.app.deviceName ?? 'iPhone';
  const [tone, key]: [string, Key] = connection === 'reconnecting' ? ['warn', 'sync.reconnecting']
    : connection === 'offline' ? ['bad', 'sync.offline']
      : online ? ['ok', 'sync.online'] : ['warn', 'sync.away'];
  return (
    <div className={`livepill livepill--${tone}`} role="status">
      <span className="livepill__dot" aria-hidden="true" />
      <IconSmartphone size="sm" />
      <span className="livepill__device">{device}</span>
      <span className="livepill__state">{t(key)}</span>
    </div>
  );
}

/** The page's own theme, as icons; the phone's appearance is a setting in the console. */
function ThemeSwitch() {
  const [theme, setTheme] = useState<Theme>(loadTheme);
  const pick = (next: Theme) => { setTheme(next); applyTheme(next); };
  const options: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: 'system', label: t('theme.system'), icon: <IconMonitor size="sm" /> },
    { value: 'light', label: t('theme.light'), icon: <IconSun size="sm" /> },
    { value: 'dark', label: t('theme.dark'), icon: <IconMoon size="sm" /> },
  ];
  return (
    <div className="seg seg--icons" role="radiogroup" aria-label={t('theme.label')}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={theme === o.value} aria-label={o.label} title={o.label}
          className={theme === o.value ? 'seg--on' : undefined} onClick={() => pick(o.value)}>{o.icon}</button>
      ))}
    </div>
  );
}

function Plain({ store, phase, renewing }: { store: SetupStore; phase: SessionPhase; renewing: boolean }) {
  const pairing = (waiting: PairingProps['pairing'], extra: Partial<PairingProps> = {}) => (
    <Pairing pairing={waiting} renewing={renewing} onExpire={() => store.expire()} onRenew={() => store.renew()}
      onAllow={() => store.allow()} onReject={() => store.reject()} {...extra} />
  );
  switch (phase.kind) {
    case 'connecting':
      return <Centered><div className="card status-line"><span className="spinner" aria-hidden="true" />{t('connect.connecting')}</div></Centered>;
    case 'waiting':
      return pairing(phase);
    case 'expired':
    case 'reconnecting':
    case 'offline':
      return pairing(null, { stop: phase.kind });
    case 'confirm':
      return pairing(null, { confirm: true });
    case 'error':
      return (
        <Centered>
          <div className="card stop">
            <div className="note note--bad">{errorText(phase.reason, phase.code)}</div>
            <div className="form-foot"><Button kind="primary" onClick={() => location.reload()}>{t('action.startOver')}</Button></div>
          </div>
        </Centered>
      );
    default:
      return <Centered><div className="card status-line"><span className="spinner" aria-hidden="true" />{t('connect.waitingPhone')}</div></Centered>;
  }
}

const ERROR_KEYS: Record<ErrorReason, Key> = {
  lost: 'lost.text', unreachable: 'lost.text', rejected: 'error.rejected', decrypt: 'error.decrypt', bye: 'error.bye',
  expired: 'error.expired', roomFull: 'error.roomFull', tooMany: 'error.tooMany', relay: 'error.relay',
};

function errorText(reason: ErrorReason, code?: string): string {
  return t(ERROR_KEYS[reason], { code: code ?? '' });
}
